import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, EyeOff, Lock, RefreshCcw, Search, Share2, ShieldCheck, Trophy } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const CONTROL_PLANS = ['immortal', 'radiant'];
const SORT_OPTIONS = [
  ['vp_desc', 'Highest VP spent'],
  ['vp_asc', 'Lowest VP spent'],
  ['newest', 'Newest account added'],
  ['oldest', 'Oldest account added'],
  ['level_desc', 'Highest account level'],
  ['level_asc', 'Lowest account level'],
];
const PRIVACY_OPTIONS = [
  ['hide_ign', 'Hide IGN'],
  ['hide_inventory', 'Hide inventory'],
  ['hide_vp_spent', 'Hide VP spent'],
  ['hide_store', 'Hide store'],
  ['hide_match_history', 'Hide match history'],
];

const LockedControlPanel = () => (
  <Layout>
    <section className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-10">
      <div className="neon-card rounded-lg p-7 max-w-lg w-full text-center border-red-900/70 bg-red-950/20">
        <Lock className="w-8 h-8 text-red-300 mx-auto mb-3" />
        <div className="font-mono-display text-2xl text-white mb-2">Control Panel Locked</div>
        <p className="text-sm text-red-200/70 mb-3">Control Panel is only available for Immortal and Radiant subscribers.</p>
        <p className="text-xs text-zinc-400 mb-5">Your previous privacy settings remain active, but editing them requires Immortal or Radiant.</p>
        <Link to="/pricing" className="btn-neon inline-flex justify-center">Upgrade Now</Link>
      </div>
    </section>
  </Layout>
);

export default function ControlPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sort, setSort] = useState('vp_desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ currentPage: 1, totalPages: 1, totalAccounts: 0, limit: 20 });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const allowed = CONTROL_PLANS.includes((user?.plan || 'free').toLowerCase());

  const fetchAccounts = async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/control-panel/accounts', { params: { sort, search, page, limit: 20 } });
      setAccounts(data.accounts || []);
      setMeta({
        currentPage: data.currentPage || 1,
        totalPages: data.totalPages || 1,
        totalAccounts: data.totalAccounts || 0,
        limit: data.limit || 20,
      });
    } catch (e) {
      if (e?.response?.status === 403) setError('locked');
      else setError(e?.response?.data?.detail || 'Failed to load Control Panel');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); /* eslint-disable-next-line */ }, [sort, search, page, allowed]);

  const updateSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const updateSort = (value) => {
    setSort(value);
    setPage(1);
  };

  const updatePrivacy = async (accountId, key, value) => {
    const { data } = await api.patch(`/control-panel/accounts/${accountId}/privacy`, { [key]: value });
    setAccounts(cur => cur.map(a => a.id === accountId ? data.account : a));
  };

  const updateVisibility = async (accountId, value) => {
    const { data } = await api.patch(`/control-panel/accounts/${accountId}/visibility`, { show_on_leaderboard: value });
    setAccounts(cur => cur.map(a => a.id === accountId ? data.account : a));
  };

  const shareAction = async (accountId, action) => {
    if (action === 'regenerate' && !window.confirm('Regenerate this share link? The old public link will stop working.')) return;
    if (action === 'deactivate' && !window.confirm('Deactivate this share link? Public visitors will see an unavailable message.')) return;
    setError('');
    try {
      const { data } = await api.post(`/control-panel/accounts/${accountId}/share/${action}`);
      setAccounts(cur => cur.map(a => a.id === accountId ? {
        ...a,
        share_slug: data.share_slug || a.share_slug,
        shareSlug: data.share_slug || a.shareSlug,
        share_active: data.share_active,
      } : a));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Share link action failed. Please try again.');
    }
  };

  if (!allowed || error === 'locked') return <LockedControlPanel />;

  return (
    <Layout>
      <section className="min-h-[calc(100vh-4rem)] px-4 py-8 grid place-items-start md:place-items-center">
        <div className="neon-card rounded-lg w-full max-w-6xl max-h-[82vh] overflow-hidden border-red-900/60 bg-red-950/10">
          <div className="px-5 py-4 border-b border-red-900/40 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded grid place-items-center border border-red-500/50 bg-red-500/10">
                <Crown className="w-5 h-5 text-red-300" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-300">Premium Dashboard</div>
                <h1 className="font-mono-display text-2xl text-white">Control Panel</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select value={sort} onChange={(e) => updateSort(e.target.value)} className="bg-black/50 border border-zinc-800 rounded px-3 py-2 text-sm text-white">
                {SORT_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
              <button onClick={fetchAccounts} className="btn-ghost" title="Refresh"><RefreshCcw className="w-3.5 h-3.5" /></button>
              <button onClick={() => navigate('/dashboard')} className="btn-ghost"><ArrowLeft className="w-3.5 h-3.5" />Back</button>
            </div>
          </div>

          <div className="p-5 overflow-y-auto max-h-[calc(82vh-5rem)]">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-red-400/70 rounded px-3 py-2">
                <Search className="w-4 h-4 text-zinc-500" />
                <input
                  value={search}
                  onChange={(e) => updateSearch(e.target.value)}
                  placeholder="Search by IGN"
                  className="bg-transparent flex-1 outline-none text-white text-sm placeholder:text-zinc-600"
                />
              </div>
              <div className="text-xs text-zinc-500 tabular">
                Page {meta.currentPage} of {meta.totalPages} // {meta.totalAccounts} accounts
              </div>
            </div>
            {error && error !== 'locked' && (
              <div className="mb-4 rounded border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
            )}
            {loading ? (
              <div className="py-12 text-center text-zinc-500 font-mono-display">Loading Control Panel...</div>
            ) : accounts.length === 0 ? (
              <div className="py-12 text-center text-zinc-500 font-mono-display">
                {search.trim() ? 'No accounts found for this search.' : 'No accounts found'}
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {accounts.map(acc => {
                  const settings = acc.share_settings || {};
                  const slug = acc.share_slug || acc.shareSlug;
                  return (
                    <div key={acc.id} className="rounded border border-zinc-800 bg-black/30 p-4">
                      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                        <div>
                          <div className="font-mono-display text-lg text-white">{acc.ign} <span className="text-orange-300">#{acc.tag}</span></div>
                          <div className="text-xs text-zinc-500">Level {acc.level} // {(acc.totalValueVP || 0).toLocaleString()} VP // {acc.addedAt ? new Date(acc.addedAt).toLocaleDateString() : 'Unknown date'}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${acc.share_active === false ? 'border-red-500/40 text-red-300 bg-red-500/10' : 'border-cyan-400/40 text-cyan-300 bg-cyan-400/10'}`}>
                            <ShieldCheck className="w-3 h-3" />{acc.share_active === false ? 'Share inactive' : 'Share active'}
                          </span>
                          {slug && <a href={`/share/${slug}`} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 !px-2 text-xs"><Share2 className="w-3.5 h-3.5" />Open</a>}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-red-300 mb-2">Share Privacy</div>
                          <div className="grid sm:grid-cols-2 gap-2">
                            {PRIVACY_OPTIONS.map(([key, label]) => (
                              <label key={key} className="flex items-center gap-2 rounded border border-zinc-800 bg-black/30 px-3 py-2 text-sm text-zinc-300">
                                <input
                                  type="checkbox"
                                  checked={!!settings[key]}
                                  onChange={(e) => updatePrivacy(acc.id, key, e.target.checked)}
                                  className="w-4 h-4 rounded border-zinc-700 bg-black accent-red-500"
                                />
                                <EyeOff className="w-3.5 h-3.5 text-red-300" />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-yellow-300 mb-2">Hall of Fame Visibility</div>
                          <div className="rounded border border-zinc-800 bg-black/30 p-3 mb-4">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div>
                                <div className="text-sm text-zinc-200 flex items-center gap-2">
                                  <Trophy className="w-4 h-4 text-yellow-300" />
                                  {acc.show_on_leaderboard ? 'Public' : 'Private'}
                                </div>
                                <p className="text-xs text-zinc-500 mt-1 font-bold">Public accounts can appear in Hall of Fame. Private accounts are hidden from leaderboard.</p>
                              </div>
                              <button
                                onClick={() => updateVisibility(acc.id, !acc.show_on_leaderboard)}
                                className={`px-3 py-1.5 rounded border text-xs uppercase tracking-widest transition-colors ${
                                  acc.show_on_leaderboard
                                    ? 'border-yellow-400/60 text-yellow-200 bg-yellow-500/10 hover:border-yellow-300'
                                    : 'border-zinc-700 text-zinc-400 hover:border-yellow-400/50 hover:text-yellow-200'
                                }`}
                              >
                                {acc.show_on_leaderboard ? 'Public' : 'Private'}
                              </button>
                            </div>
                          </div>

                          <div className="text-[10px] uppercase tracking-widest text-red-300 mb-2">Share Link</div>
                          <div className="rounded border border-zinc-800 bg-black/30 p-3">
                            <code className="block text-xs text-zinc-400 truncate mb-3">{slug ? `${window.location.origin}/share/${slug}` : 'No share link generated'}</code>
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => shareAction(acc.id, 'generate')} className="btn-ghost !py-1.5 !px-2 text-xs">Generate</button>
                              <button onClick={() => shareAction(acc.id, 'regenerate')} className="btn-ghost !py-1.5 !px-2 text-xs">Regenerate</button>
                              <button onClick={() => shareAction(acc.id, 'deactivate')} className="border border-red-500/40 hover:border-red-400 hover:bg-red-500/10 text-red-300 rounded px-2 py-1.5 text-xs uppercase tracking-widest transition-colors">Deactivate</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={meta.currentPage <= 1 || loading}
                    className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="text-xs text-zinc-400 tabular">Page {meta.currentPage} of {meta.totalPages}</div>
                  <button
                    onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
                    disabled={meta.currentPage >= meta.totalPages || loading}
                    className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
